import { cn } from '@shared/lib/cn'
import { FullscreenMenu } from '@shared/ui/fullscreen-menu'
import { Link, useNavigate, useRouter } from '@tanstack/react-router'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ReactNode, useState } from 'react'
import { RiArrowLeftSLine, RiHomeFill, RiMenu2Fill } from 'react-icons/ri'

const headerVariants = cva('sticky top-0 z-sticky', {
  variants: {
    variant: {
      blur: 'backdrop-blur-md',
      transparent: '',
      solid: 'bg-background',
    },
    divider: {
      none: '',
      border: 'border-b border-border',
      elevation: 'shadow-(--elevation-sm)',
    },
  },
  defaultVariants: {
    variant: 'blur',
    divider: 'none',
  },
})

const BTN_CLASS =
  'flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/30'

const SPACER = <div className="size-11" />

/* ─── Compound 버튼 컴포넌트 ─── */

interface BackProps {
  readonly onBack?: () => void
}

function Back({ onBack }: BackProps) {
  const router = useRouter()
  const navigate = useNavigate()

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    if (router.history.length > 1) {
      router.history.back()
    } else {
      void navigate({ to: '/saju/$sajuType', params: { sajuType: 'classic' } })
    }
  }

  return (
    <button type="button" onClick={handleBack} className={BTN_CLASS} aria-label="뒤로가기">
      <RiArrowLeftSLine size={24} aria-hidden="true" />
    </button>
  )
}

interface BackLinkProps {
  readonly to: string
  readonly params?: Record<string, string>
}

function BackLink({ to, params }: BackLinkProps) {
  return (
    <Link to={to} params={params} className={BTN_CLASS} aria-label="뒤로가기">
      <RiArrowLeftSLine size={24} aria-hidden="true" />
    </Link>
  )
}

function Home() {
  const navigate = useNavigate()

  return (
    <button
      type="button"
      onClick={() => {
        void navigate({ to: '/saju/$sajuType', params: { sajuType: 'classic' } })
      }}
      className={BTN_CLASS}
      aria-label="홈으로"
    >
      <RiHomeFill size={22} aria-hidden="true" />
    </button>
  )
}

function MenuButton() {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <>
      <FullscreenMenu
        isOpen={menuOpen}
        onClose={() => {
          setMenuOpen(false)
        }}
      />
      <button
        type="button"
        className={BTN_CLASS}
        aria-label="메뉴"
        onClick={() => {
          setMenuOpen(true)
        }}
      >
        <RiMenu2Fill size={22} aria-hidden="true" />
      </button>
    </>
  )
}

/* ─── AppHeader ─── */

interface AppHeaderProps extends VariantProps<typeof headerVariants> {
  /** 좌측 영역. 없으면 spacer */
  readonly leading?: ReactNode
  /** 중앙 타이틀 */
  readonly title?: string
  /** 우측 영역. 없으면 spacer */
  readonly trailing?: ReactNode
  /** 헤더 하단에 추가 콘텐츠 (예: progress bar) */
  readonly children?: ReactNode
}

function AppHeader({ leading, title, trailing, variant, divider, children }: AppHeaderProps) {
  return (
    <div className={cn(headerVariants({ variant, divider }))}>
      <header className="flex h-14 items-center justify-between gap-2 px-3">
        {leading ?? SPACER}
        {title && (
          <h1 className="flex-1 truncate text-center typo-body font-bold text-foreground">
            {title}
          </h1>
        )}
        {trailing ?? SPACER}
      </header>
      {children}
    </div>
  )
}

AppHeader.Back = Back
AppHeader.BackLink = BackLink
AppHeader.Home = Home
AppHeader.Menu = MenuButton

export { AppHeader, BTN_CLASS as headerBtnClass }
